import { ChatThreadTypeEnum } from 'common-utils';
import { ChatThreadRI, SectionEnrollmentRI, StudentContactInfo } from 'common-utils/rest-interfaces';
import { Observable } from 'rxjs';
import { of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { NavItem } from './interfaces/nav-item';
import { SearchFavorites } from './interfaces/search-favorites';
import { StudentSearchResult } from './interfaces/search-result';
import { PdsWidget } from './pds-widget';
import { ChatThreadService } from './services/chat-thread-service';
import { PdsNotificationEvent } from './interfaces/pds-notification-event';

export class PdsSearch extends PdsWidget {
  is = 'pds-search';
  dataHasFavorites: string;
  id: string;
  favoriteMap: { [uid: string]: boolean };  // used to keep track of which results are favorites
  isFocused = false;
  navItems: Array<NavItem>;
  navigationSearchError: boolean;
  getS3MediaPath: () => string;
  searchQuery: string;
  students: Array<StudentSearchResult>;
  studentSearchError: boolean;
  currentUserUid?: string;
  useNavIcons: boolean;

  quickMessageDialogThreadRI: ChatThreadRI;
  sectionUids: Array<string>;
  isQuickMessageDialogOpen: boolean;
  chatTopic: StudentSearchResult;

  studentsWithParentsContactInfo: Array<StudentContactInfo>;
  isParentContactInfo: boolean;

  properties = {
    dataHasFavorites: { // this is here so that the new functionality doesn't exist by default (backwards compatibility)
      type: String,
      value: 'false'
    },
    id: {
      type: String,
      value: ''
    }
  };

  private _chatThreadService = new ChatThreadService();
  private _favorites: SearchFavorites;  // never mutate this object, always assign to new object
  // if assigning directly make sure favoriteMap is synced

  get favorites(): SearchFavorites {
    return this._favorites;
  }

  set favorites(value: SearchFavorites) {
    this._favorites = value;
    this.generateFavoritesMap();
  }

  getId(id: string): string {
    return 'search-input' + id;
  }

  attached(): void {
    super.attached();
    document.body.addEventListener('click', () => {
      this.searchQuery = '';
      this.isFocused = false;
    });
    this.querySelector('input').addEventListener('focus', () => {
      this.isFocused = true;
    });
  }

  ready(): void {
    super.ready();
    this.favorites = {
      links: [],
      students: [],
      assignments: []
    };
    this.favoriteMap = {};
    this.isFocused = false;
    this.navItems = [];
    this.navigationSearchError = false;
    this.getS3MediaPath = this.pds.preferences.getS3MediaPath;
    this.searchQuery = '';
    this.students = [];
    this.studentSearchError = false;
    this.useNavIcons = true;
  }

  iconClickHandler(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.querySelector('input').focus();
  }

  inputChange(event: Event): void {
    this.searchQuery = (<any>event.target).value;
    this.debounce('pdsInputChangeFire', function (): void {
      this.fire('pdsInputChange', this.searchQuery.trim());
    }, 250);
  }

  getInputClass(searchQuery): string {
    return searchQuery != null && searchQuery.trim().length > 0 ? 'expanded' : '';
  }

  isSearchQueryOverLimit(searchQuery): boolean {
    return searchQuery != null && searchQuery.trim().length > 1;
  }

  showNoResultsMessage(students: Array<StudentSearchResult>, navItems: Array<NavItem>, studentSearchError: boolean, navigationSearchError: boolean): boolean {
    if (students instanceof Array && navItems instanceof Array) {
      return (students.length + navItems.length === 0) && !(studentSearchError || navigationSearchError);
    }
    return false;
  }

  showResultsSection(results: Array<any>, resultsSectionError: boolean): boolean {
    return results.length > 0 || resultsSectionError;
  }

  useSpecificNavItemIcon(navItem: NavItem, useNavIcons: boolean): boolean {
    return useNavIcons && navItem.iconName != null;
  }

  useGenericNavItemIcon(navItem: NavItem, useNavIcons: boolean): boolean {
    return useNavIcons && navItem.iconName == null;
  }

  studentClickHandler(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    let target = <HTMLElement & { dataStudent: StudentSearchResult }>event.target;
    if (!this.pds.utils.is(target, 'a')) {
      target = <HTMLElement & { dataStudent: StudentSearchResult }>this.pds.utils.getClosest(target, 'a');
    }
    let student: any = target.dataStudent;
    this.fire('pdsStudentClick', student);
    this.searchQuery = ''; // close the dialog
    this.isFocused = false;
  }

  navItemClickHandler(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    let navItem: NavItem = event.model.navItem; //polymer
    if (navItem.onUserClick) {
      navItem.onUserClick(navItem, event);
    } else {
      console.error('nav item clicked with no click event');
    }
    this.searchQuery = '';  // close the dialog
    this.isFocused = false;
  }

  getTooltipForNavItem(navItem: NavItem, redrawTrigger: boolean): string {
    if (navItem.context.parentLinkName != null) {
      return `${this.pds.i18n.getText(navItem.name)} (${this.pds.i18n.getText(navItem.context.parentLinkName)})`;
    }
    return `${this.pds.i18n.getText(navItem.name)}`;
  }

  generateFavoritesMap(): void {
    this.favoriteMap = {};
    for (let link of this._favorites.links) {
      if (link.context == null) {
        throw 'pds-search nav link has no context';
      }
      this.favoriteMap[link.context.uid] = true;
    }
    for (let student of this._favorites.students) {
      this.favoriteMap[student.uid] = true;
    }
    this.resetFavorites();
  }

  getFavoriteClass(valueWithUid: { uid: string }, favorites: SearchFavorites): string {
    return this.favoriteMap[valueWithUid.uid] != null ? 'pds-search-favorite' : '';
  }

  showFavorites(searchQuery, isFocused, favorites: SearchFavorites): boolean {
    let hasFavorites = this.dataHasFavorites === 'true' && this.favorites != null && (
      this.favorites.assignments != null && this.favorites.assignments.length > 0
      || this.favorites.students != null && this.favorites.students.length > 0
      || this.favorites.links != null && this.favorites.links.length > 0
    );
    return !this.isSearchQueryOverLimit(searchQuery) && isFocused && hasFavorites;
  }

  toggleFavoriteLink(event): void {
    event.preventDefault();
    event.stopPropagation();
    let navItem: NavItem = event.model.navItem; //polymer
    if (this.favoriteMap[navItem.context.uid] != null) {
      delete this.favoriteMap[navItem.context.uid];
      this.favorites.links = this.favorites.links.filter((link) => {
        return link.context.uid !== navItem.context.uid;
      });

      this.fire('pdsSearchRemoveFavorite', {
        index: 'links',
        uid: navItem.context.uid
      });
    } else {
      this.favoriteMap[navItem.context.uid] = true;
      this.favorites.links.push(navItem);
      this.fire('pdsSearchAddFavorite', {
        index: 'links',
        uid: navItem.context.uid
      });
    }
    this.resetFavorites();
  }

  toggleFavoriteStudent(event): void {
    event.preventDefault();
    event.stopPropagation();
    let student: StudentSearchResult = event.model.student; //polymer
    if (this.favoriteMap[student.uid] != null) {
      delete this.favoriteMap[student.uid];
      this.favorites.students = this.favorites.students.filter((_student) => {
        return student.uid !== _student.uid;
      });

      this.fire('pdsSearchRemoveFavorite', {
        index: 'students',
        uid: student.uid
      });
    } else {
      this.favoriteMap[student.uid] = true;
      this.favorites.students.push(student);
      this.fire('pdsSearchAddFavorite', {
        index: 'students',
        uid: student.uid
      });
    }
    this.resetFavorites();
  }

  resetFavorites(): void {
    this._favorites = { // triggers change detection in dom-repeat
      links: Array.from(this._favorites.links),
      students: Array.from(this._favorites.students),
      assignments: Array.from(this._favorites.assignments)
    };
  }

  handleChatNavigationEvent(event: CustomEvent): void {
    event.preventDefault();
    let chatThread: ChatThreadRI = event.detail;
    let notificationEvent: PdsNotificationEvent = {
      type: 'COMMUNICATION_NOTIFICATION', // default attriute for chat message navigation event
      shouldNavigate: true, // default attriute for chat message navigation event
      topicUid: chatThread.topicUid,
      threadOwnerUid: chatThread.ownerUid,
      threadType: chatThread.threadType
    };

    this.fire('pdsNotificationClick', notificationEvent);
  }

  emitClickEvent(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.currentUserUid == null) {
      return;
    }

    this.chatTopic = event.model.student;
    this.sectionUids = this.chatTopic.sectionEnrollments.map((se: SectionEnrollmentRI) => se.sectionUid);
    let thread: any = {
      name: this.chatTopic.firstName + ' ' + this.chatTopic.lastName,
      threadType: ChatThreadTypeEnum.STUDENT_TEACHER,
      ownerUid: this.currentUserUid,
      ownerUserType: 'TEACHER',
      topicUid: this.chatTopic.uid,
      participants: [{ userUid: this.chatTopic.uid, userType: 'STUDENT' }, { userUid: this.currentUserUid, userType: 'TEACHER' }]
    };
    this._chatThreadService.fetchTopicThread(this.pds.preferences.host, thread)
      .subscribe((chatThread: ChatThreadRI) => {
        this.isParentContactInfo = this.checkForParentContactInfo(this.chatTopic.uid);
        this.quickMessageDialogThreadRI = chatThread;
        this.isQuickMessageDialogOpen = true;
        setTimeout(() => {
          this.querySelector('pds-chat-quick-message').addEventListener('pdsRemoveDialog', () => {
            this.isQuickMessageDialogOpen = false;
          });
        });
      });
  }

  getTopicUserName(): string {
    return this.chatTopic.firstName + ' ' + this.chatTopic.lastName;
  }

  handleChatTabChange(event): void {
    let threadObs: Observable<ChatThreadRI>;
    let thread: any = {
      name: this.quickMessageDialogThreadRI.name,
      ownerUid: this.quickMessageDialogThreadRI.ownerUid,
      ownerUserType: this.quickMessageDialogThreadRI.ownerUserType,
      topicUid: this.quickMessageDialogThreadRI.topicUid
    };

    if (event.detail === 'STUDENT_TAB') {
      threadObs = of([{ userUid: this.quickMessageDialogThreadRI.topicUid, userType: 'STUDENT' }, { userUid: this.quickMessageDialogThreadRI.ownerUid, userType: 'TEACHER' }]).pipe(
        mergeMap((participants) => {
          thread.participants = participants;
          thread.threadType = ChatThreadTypeEnum.STUDENT_TEACHER;
          return this._chatThreadService.fetchTopicThread(this.pds.preferences.host, thread);
        }));
    } else {
      threadObs = this._chatThreadService.fetchParentParticipants(this.pds.preferences.host, this.quickMessageDialogThreadRI.topicUid!).pipe(
        mergeMap((participants) => {
          participants.push({ userUid: this.quickMessageDialogThreadRI.ownerUid, userType: 'TEACHER' });
          thread.participants = participants;
          thread.threadType = ChatThreadTypeEnum.TEACHER_PARENT;
          return this._chatThreadService.fetchTopicThread(this.pds.preferences.host, thread);
        }));
    }
    threadObs
      .subscribe((chatThread: ChatThreadRI) => {
        this.quickMessageDialogThreadRI = chatThread;
        setTimeout(() => {
          let containerEl = <HTMLElement>this.querySelector('.pds-chat-message-container');
          containerEl.scrollTop = containerEl.scrollHeight;
        });
      }, (err) => {
        console.warn(err);
      });
  }

  checkForParentContactInfo(selectedStudentUid: string): boolean {
    // studentsWithParentsContactInfo is the list of students with parent contact
    // check the selected studentUid is there in the studentsWithParentsContactInfo list.
    let haveParentContactInfo: boolean = this.studentsWithParentsContactInfo.some((studentListItem) => {
      return studentListItem.studentUid === selectedStudentUid;
    });
    return haveParentContactInfo;
  }
}
